import { useState } from 'react'
import { Link } from 'react-router-dom'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'
import LevelPill from '../../components/badges/LevelPill'
import RoleBadge from './components/RoleBadge'
import PageMasthead from '../../components/PageMasthead'
import ClassifierConfigTable from './components/ClassifierConfigTable'
import TabBar from './components/TabBar'
import { ClassifierCoverageRow } from './types'
import { PROFILES, USERS } from './mockData'
import { WIREFRAME_PATHS, userDetailPath } from './routes'

// The team this page represents. Wireframe pages are fixed to a single example.
const TEAM_ID = 'acme-social'
const TEAM_NAME = 'Acme — Social'
const TEAM_PROFILE_COUNT = 42

// Aggregated view of this team's 42 profiles: the union of classifiers those
// profiles have, with coverage. Hate & Harassment shows a gap — 4 profiles were
// added to the team after the classifier was bulk-added, and there is no
// inheritance to pick them up automatically.
const rows: ClassifierCoverageRow[] = [
    { classifierId: 'hate-harassment', liveVersion: 3, appliedProfiles: 38, totalProfiles: 42 },
    { classifierId: 'spam-scams', liveVersion: 6, appliedProfiles: 42, totalProfiles: 42 },
    { classifierId: 'competitor-mentions', liveVersion: 1, appliedProfiles: 42, totalProfiles: 42 }
]

// Profiles belonging to this team — the team page is the way into profile config.
const teamProfiles = PROFILES.filter((p) => p.teamId === TEAM_ID)

// People assigned to this team: members of the team plus org-wide admins.
const teamUsers = USERS.filter((u) => u.allTeams || u.teams.includes(TEAM_NAME))

type Tab = 'Profiles' | 'Users' | 'Classifiers'
const TABS: Tab[] = ['Profiles', 'Users', 'Classifiers']

export default function TeamConfigPage({ initialTab = 'Profiles' }: { initialTab?: Tab }) {
    const [activeTab, setActiveTab] = useState<Tab>(initialTab)

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            <WireframeBanner />

            <PageDesignNote type="question">
                Classifiers live on profiles; this page aggregates them. Adding here bulk-adds to
                the team's current profiles — new profiles don't inherit team classifiers, so
                coverage gaps surface here for SD to fix. Is a gap acceptable until someone acts,
                or should profile onboarding prompt for classifiers?
            </PageDesignNote>

            <PageMasthead
                breadcrumb={
                    <>
                        <Link to={WIREFRAME_PATHS.orgList} className="text-arwen-700 hover:underline">
                            Organisations
                        </Link>{' '}
                        /{' '}
                        <Link to={WIREFRAME_PATHS.orgConfig} className="text-arwen-700 hover:underline">
                            Acme Corp
                        </Link>
                    </>
                }
                title={TEAM_NAME}
                levelPill={<LevelPill level="team" />}
                subtitle={`${TEAM_PROFILE_COUNT} profiles · ${teamUsers.length} users`}
            />

            <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

            {/* Tab content */}
            <div className="flex-1 overflow-auto px-6 py-5">
                {activeTab === 'Profiles' && (
                    /* Profiles in this team — entry point into profile config */
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                            <span className="text-[13px] font-semibold text-gray-900">
                                Profiles in this team
                            </span>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-normal text-gray-800">
                                    Showing {teamProfiles.length} of {TEAM_PROFILE_COUNT}
                                </span>
                                <button
                                    type="button"
                                    className="rounded border border-arwen-200 bg-arwen-50 hover:bg-arwen-100 px-2.5 py-1 text-xs font-semibold text-arwen-700"
                                >
                                    + Add profile
                                </button>
                            </div>
                        </div>
                        <div className="divide-y divide-dashed divide-gray-100 px-4 py-1">
                            {teamProfiles.map((profile) => (
                                <div
                                    key={profile.id}
                                    className="flex items-center justify-between py-2.5"
                                >
                                    <Link
                                        to={WIREFRAME_PATHS.profileConfig}
                                        className="text-sm font-semibold text-arwen-700 hover:underline"
                                    >
                                        {profile.name}
                                    </Link>
                                    <span className="text-sm text-gray-800">{profile.platform}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'Users' && (
                    /* Users assigned to this team */
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                            <span className="flex items-center text-[13px] font-semibold text-gray-900">
                                Users in this team
                                <span className="ml-2 text-xs font-normal text-gray-800">
                                    {teamUsers.length}
                                </span>
                            </span>
                            <button
                                type="button"
                                className="rounded border border-arwen-200 bg-arwen-50 hover:bg-arwen-100 px-2.5 py-1 text-xs font-semibold text-arwen-700"
                            >
                                + Add user
                            </button>
                        </div>
                        <table className="w-full border-collapse text-sm">
                            <tbody className="divide-y divide-gray-50">
                                {teamUsers.map((u) => (
                                    <tr key={u.id}>
                                        <td className="px-4 py-2.5">
                                            <Link
                                                to={userDetailPath(u.id)}
                                                className="font-medium text-arwen-700 hover:underline"
                                            >
                                                {u.name}
                                            </Link>
                                            <div className="text-[11.5px] text-gray-800">{u.email}</div>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <RoleBadge role={u.role} />
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-xs text-gray-800">
                                            {u.allTeams ? 'Org admin · all teams' : 'Team member'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="border-t border-gray-100 px-4 py-2.5 text-[11.5px] text-gray-800">
                            Org admins have access to every team. Manage members on the{' '}
                            <Link to={WIREFRAME_PATHS.orgUsers} className="text-arwen-700 hover:underline">
                                Users &amp; access
                            </Link>{' '}
                            tab.
                        </div>
                    </div>
                )}

                {activeTab === 'Classifiers' && (
                    <div className="rounded-lg border border-gray-200 bg-white">
                        {/* Card header */}
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                            <span className="text-[13px] font-semibold text-gray-900">
                                Classifiers across this team's profiles
                            </span>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-normal text-gray-800">
                                    {rows.length} classifiers across {TEAM_PROFILE_COUNT} profiles
                                </span>
                                <button
                                    type="button"
                                    className="rounded border border-arwen-200 bg-arwen-50 hover:bg-arwen-100 px-2.5 py-1 text-xs font-semibold text-arwen-700"
                                >
                                    + Add classifier
                                </button>
                            </div>
                        </div>

                        <ClassifierConfigTable level="team" rows={rows} />
                    </div>
                )}
            </div>
        </div>
    )
}
